/**
 * Centralized Design System & Color Palette for Landing & Studio
 *
 * Changing any color token in this file will automatically update all
 * matching backgrounds, buttons, cards, borders, and accents across
 * both the Landing page and Studio editor.
 */

export const themeColors = {
  // Page, Header, and Card Backgrounds
  backgrounds: {
    landing: "#030712", // Main landing page backdrop (deep void)
    landingHeader: "rgba(3, 7, 18, 0.90)", // Sticky navbar backdrop
    landingCard: "rgba(9, 13, 26, 0.80)", // Glass card backdrop
    landingCardElevated: "#0d1226", // Featured / Pro+ card backdrop
    studio: "#07090e", // Studio workspace backdrop
    studioHeader: "rgba(9, 9, 11, 0.80)", // Studio top ingestion bar
    studioCard: "rgba(24, 24, 27, 0.80)", // Studio customization panel card
    studioCanvas: "rgba(9, 9, 11, 0.60)", // Studio preview frame container
  },

  // Interactive Buttons & CTAs
  buttons: {
    // Primary CTA (e.g. Studio launch, schedule post, pro trials)
    primary: {
      bg: "#4f46e5", // Indigo 600
      hover: "#6366f1", // Indigo 500
      text: "#ffffff",
      shadow: "rgba(79, 70, 229, 0.30)",
    },
    // Action Blue (e.g. Stripe checkout, Get Started free, Pro plan CTA)
    action: {
      bg: "#2563eb", // Blue 600
      hover: "#3b82f6", // Blue 500
      text: "#ffffff",
      shadow: "rgba(37, 99, 235, 0.25)",
    },
    // Studio Generate Button Gradient
    generate: {
      from: "#06b6d4", // Cyan 500
      to: "#2563eb", // Blue 600
      hoverFrom: "#22d3ee", // Cyan 400
      hoverTo: "#3b82f6", // Blue 500
      text: "#ffffff",
      shadow: "rgba(6, 182, 212, 0.20)",
    },
    // Secondary / Outline buttons
    secondary: {
      bg: "rgba(255, 255, 255, 0.05)",
      hover: "rgba(255, 255, 255, 0.10)",
      border: "rgba(255, 255, 255, 0.10)",
      text: "#e4e4e7",
    },
  },

  // Borders & Dividers
  borders: {
    subtle: "rgba(255, 255, 255, 0.08)",
    subtleStudio: "rgba(255, 255, 255, 0.05)",
    hover: "rgba(255, 255, 255, 0.20)",
    highlight: "#6366f1", // Indigo highlight border
    focus: "#22d3ee", // Cyan focus ring
  },

  // Brand Accents
  accents: {
    cyan: "#22d3ee", // Studio highlight & verified badge
    blue: "#3b82f6", // Twitter bird & info links
    indigo: "#6366f1", // Primary tag & pill badge
    purple: "#a855f7", // Gradient highlight secondary
    emerald: "#34d399", // Success checkmarks & annual discount
    red: "#f87171", // Destructive / cancel / errors
  },

  // Typography Colors
  text: {
    primary: "#ffffff",
    secondary: "#a1a1aa", // Zinc 400
    muted: "#71717a", // Zinc 500
    dim: "#52525b", // Zinc 600
  },
};

/**
 * Applies all theme tokens as CSS Custom Properties (--theme-...)
 * on document.documentElement so that CSS and Tailwind classes
 * automatically synchronize with these values in real-time.
 */
export const applyThemeColors = () => {
  if (typeof document === "undefined" || !document.documentElement) return;

  const root = document.documentElement;

  // Backgrounds
  root.style.setProperty("--theme-landing-bg", themeColors.backgrounds.landing);
  root.style.setProperty("--theme-landing-header-bg", themeColors.backgrounds.landingHeader);
  root.style.setProperty("--theme-landing-card-bg", themeColors.backgrounds.landingCard);
  root.style.setProperty("--theme-landing-card-elevated-bg", themeColors.backgrounds.landingCardElevated);
  root.style.setProperty("--theme-studio-bg", themeColors.backgrounds.studio);
  root.style.setProperty("--theme-studio-header-bg", themeColors.backgrounds.studioHeader);
  root.style.setProperty("--theme-studio-card-bg", themeColors.backgrounds.studioCard);
  root.style.setProperty("--theme-studio-canvas-bg", themeColors.backgrounds.studioCanvas);

  // Buttons
  root.style.setProperty("--theme-btn-primary-bg", themeColors.buttons.primary.bg);
  root.style.setProperty("--theme-btn-primary-hover", themeColors.buttons.primary.hover);
  root.style.setProperty("--theme-btn-primary-text", themeColors.buttons.primary.text);
  root.style.setProperty("--theme-btn-primary-shadow", themeColors.buttons.primary.shadow);

  root.style.setProperty("--theme-btn-action-bg", themeColors.buttons.action.bg);
  root.style.setProperty("--theme-btn-action-hover", themeColors.buttons.action.hover);
  root.style.setProperty("--theme-btn-action-text", themeColors.buttons.action.text);
  root.style.setProperty("--theme-btn-action-shadow", themeColors.buttons.action.shadow);

  root.style.setProperty("--theme-btn-generate-from", themeColors.buttons.generate.from);
  root.style.setProperty("--theme-btn-generate-to", themeColors.buttons.generate.to);
  root.style.setProperty("--theme-btn-generate-hover-from", themeColors.buttons.generate.hoverFrom);
  root.style.setProperty("--theme-btn-generate-hover-to", themeColors.buttons.generate.hoverTo);

  // Borders
  root.style.setProperty("--theme-border-subtle", themeColors.borders.subtle);
  root.style.setProperty("--theme-border-subtle-studio", themeColors.borders.subtleStudio);
  root.style.setProperty("--theme-border-hover", themeColors.borders.hover);
  root.style.setProperty("--theme-border-highlight", themeColors.borders.highlight);
  root.style.setProperty("--theme-border-focus", themeColors.borders.focus);

  // Accents
  root.style.setProperty("--theme-accent-cyan", themeColors.accents.cyan);
  root.style.setProperty("--theme-accent-blue", themeColors.accents.blue);
  root.style.setProperty("--theme-accent-indigo", themeColors.accents.indigo);
  root.style.setProperty("--theme-accent-purple", themeColors.accents.purple);
  root.style.setProperty("--theme-accent-emerald", themeColors.accents.emerald);
  root.style.setProperty("--theme-accent-red", themeColors.accents.red);

  // Text
  root.style.setProperty("--theme-text-primary", themeColors.text.primary);
  root.style.setProperty("--theme-text-secondary", themeColors.text.secondary);
  root.style.setProperty("--theme-text-muted", themeColors.text.muted);
};

export default themeColors;
